import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ThumbsUp, Meh, ThumbsDown, Users, Search, ChevronDown, ChevronUp, MessageSquare, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, PieChart, Pie } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `$${Math.round(v).toLocaleString()}`;

const CategoryBadge = ({ category, score }) => {
  const config = {
    Promoter: { icon: ThumbsUp, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    Passive: { icon: Meh, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    Detractor: { icon: ThumbsDown, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  };
  const c = config[category] || config.Passive;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${c.bg} ${c.text} ${c.border}`}>
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {score !== undefined ? score.toFixed(1) : category}
    </span>
  );
};

const ScoreBar = ({ score }) => {
  const pct = (score / 10) * 100;
  const color = score >= 9 ? '#10b981' : score >= 7 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-[#F0F0F0] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color }}>{score.toFixed(1)}</span>
    </div>
  );
};

// Compact gross-margin cell: profit dollars + margin % pill, with risk colouring
// that makes profitable detractors vs loss-making detractors obvious at a glance.
const MarginCell = ({ profit, margin }) => {
  if (profit == null) return <span className="text-[#A3A3A3]">—</span>;
  const isLoss = profit < 0;
  const isThin = !isLoss && margin != null && margin < 15;
  const isHealthy = !isLoss && margin != null && margin >= 15;
  const pillBg = isLoss ? 'bg-red-100 text-red-700 border-red-200'
    : isThin ? 'bg-amber-100 text-amber-700 border-amber-200'
    : isHealthy ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : 'bg-[#F5F5F5] text-[#737373] border-[#E5E5E5]';
  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className={`text-xs font-mono ${isLoss ? 'text-red-600 font-semibold' : 'text-[#525252]'}`}>
        ${Math.round(profit).toLocaleString()}
      </span>
      {margin != null && (
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${pillBg}`}>
          {margin.toFixed(0)}%
        </span>
      )}
    </div>
  );
};

const NPSAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('groups');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nps-analysis`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch NPS data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    const items = activeTab === 'managers' ? data.byManager : activeTab === 'groups' ? data.byGroup : data.byPlan;
    return items.filter(item => {
      if (filterCategory !== 'all') {
        const cat = item.category || (item.avgScore >= 9 ? 'Promoter' : item.avgScore >= 7 ? 'Passive' : 'Detractor');
        if (cat !== filterCategory) return false;
      }
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        const searchable = [item.manager, item.planNumber, item.propertyName, item.basePlan].filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(t)) return false;
      }
      return true;
    });
  }, [data, activeTab, filterCategory, searchTerm]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" /></div>;
  }
  if (!data) return null;

  const { summary } = data;
  const pieData = [
    { name: 'Promoters', value: summary.promoters, color: '#10b981' },
    { name: 'Passives', value: summary.passives, color: '#f59e0b' },
    { name: 'Detractors', value: summary.detractors, color: '#ef4444' },
  ];

  // Score distribution for bar chart
  const scoreDistribution = Array.from({ length: 11 }, (_, i) => ({
    score: i,
    count: data.byPlan.filter(p => Math.round(p.score) === i).length,
    color: i >= 9 ? '#10b981' : i >= 7 ? '#f59e0b' : '#ef4444',
  }));

  // Manager chart data (sorted by score)
  const managerChartData = data.byManager.slice().sort((a, b) => b.avgScore - a.avgScore).slice(0, 15);

  return (
    <div className="space-y-6" data-testid="nps-analysis-view">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Client NPS Analysis</h1>
        <p className="text-sm text-[#737373] mt-1">Client satisfaction insights across plans, groups, and managers.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">Avg Score</p>
          <p className="text-3xl font-bold text-[#171717] mt-1">{summary.avgScore}</p>
          <p className="text-[10px] text-[#A3A3A3]">out of 10</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-[10px] font-semibold text-[#A3A3A3] uppercase tracking-wider">eNPS</p>
          <p className={`text-3xl font-bold mt-1 ${summary.enps >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{summary.enps > 0 ? '+' : ''}{summary.enps}</p>
          <p className="text-[10px] text-[#A3A3A3]">{summary.totalResponses} responses</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Promoters</p>
          <p className="text-3xl font-bold text-emerald-600 mt-1">{summary.promoters}</p>
          <p className="text-[10px] text-emerald-400">{summary.promoterPct}% of responses</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Passives</p>
          <p className="text-3xl font-bold text-amber-600 mt-1">{summary.passives}</p>
          <p className="text-[10px] text-amber-400">{summary.passivePct}%</p>
        </div>
        <div className="bg-white border border-red-200 rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider">Detractors</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{summary.detractors}</p>
          <p className="text-[10px] text-red-400">{summary.detractorPct}%</p>
        </div>
        <div className="bg-white border border-red-200 rounded-[10px] p-4 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider">Detractor Revenue</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{fmt(data.byPlan.filter(p => p.category === 'Detractor').reduce((s, p) => s + p.groupRevenue, 0))}</p>
          <p className="text-[10px] text-red-400">at risk</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie Chart */}
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h2 className="text-base font-semibold text-[#171717] font-heading">Sentiment Split</h2>
          </div>
          <div className="p-4">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" strokeWidth={2} stroke="#fff">
                    {pieData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-2 shadow-lg text-sm">
                        <p className="font-semibold" style={{ color: payload[0].payload.color }}>{payload[0].name}: {payload[0].value}</p>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {pieData.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-[11px] text-[#737373]">{d.name} ({d.value})</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Score Distribution */}
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h2 className="text-base font-semibold text-[#171717] font-heading">Score Distribution</h2>
          </div>
          <div className="p-4">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scoreDistribution} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="score" tick={{ fontSize: 10, fill: '#737373' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#737373' }} axisLine={false} tickLine={false} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-2 shadow-lg text-sm">
                        <p className="font-semibold text-[#171717]">Score {payload[0].payload.score}: {payload[0].value} plans</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {scoreDistribution.map(entry => <Cell key={entry.score} fill={entry.color} opacity={0.7} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Manager NPS Chart */}
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <h2 className="text-base font-semibold text-[#171717] font-heading">NPS by Manager</h2>
          </div>
          <div className="p-4">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={managerChartData} layout="vertical" margin={{ top: 5, right: 10, left: 80, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 10, fill: '#737373' }} />
                  <YAxis dataKey="manager" type="category" tick={{ fontSize: 9, fill: '#737373' }} width={75} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-white border border-[#E5E5E5] rounded-[8px] p-2 shadow-lg text-sm">
                        <p className="font-semibold text-[#171717]">{d.manager}</p>
                        <p>Score: {d.avgScore} | eNPS: {d.enps}</p>
                        <p className="text-[#737373] text-xs">{d.promoters}P / {d.passives}Pa / {d.detractors}D</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="avgScore" radius={[0, 3, 3, 0]} barSize={14}>
                    {managerChartData.map(entry => (
                      <Cell key={entry.manager} fill={entry.avgScore >= 9 ? '#10b981' : entry.avgScore >= 7 ? '#f59e0b' : '#ef4444'} opacity={0.7} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + Filter + Search */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center rounded-[8px] border border-[#E5E5E5] p-1 bg-[#F8F9FA]">
          {[
            { key: 'groups', label: 'By OC Group' },
            { key: 'managers', label: 'By Manager' },
            { key: 'plans', label: 'By Plan' },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => { setActiveTab(tab.key); setExpandedItem(null); }}
              className={`px-4 py-2 rounded-[6px] text-xs font-medium transition-colors
                ${activeTab === tab.key ? 'bg-white text-[#171717] shadow-sm border border-[#E5E5E5]' : 'text-[#737373] hover:text-[#171717]'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-[8px] border border-[#E5E5E5] p-0.5 bg-[#F8F9FA]">
            {['all', 'Detractor', 'Passive', 'Promoter'].map(f => (
              <button
                key={f}
                onClick={() => setFilterCategory(f)}
                className={`px-3 py-1.5 rounded-[6px] text-[10px] font-semibold uppercase tracking-wider transition-colors
                  ${filterCategory === f ? 'bg-white text-[#171717] shadow-sm border border-[#E5E5E5]' : 'text-[#737373] hover:text-[#171717]'}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
            <input
              data-testid="nps-search"
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-[8px] bg-[#F8F9FA] text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] w-48"
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F8F9FA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                {activeTab === 'managers' ? (
                  <>
                    <th className="px-4 py-2.5 text-left">Manager</th>
                    <th className="px-3 py-2.5 text-center">Plans</th>
                    <th className="px-3 py-2.5 text-center w-32">Avg Score</th>
                    <th className="px-3 py-2.5 text-center">eNPS</th>
                    <th className="px-3 py-2.5 text-center">Promoters</th>
                    <th className="px-3 py-2.5 text-center">Passives</th>
                    <th className="px-3 py-2.5 text-center">Detractors</th>
                    <th className="px-3 py-2.5 text-right">Revenue</th>
                    <th className="px-3 py-2.5 text-right">Gross Margin</th>
                    <th className="px-2 py-2.5 w-8"></th>
                  </>
                ) : activeTab === 'groups' ? (
                  <>
                    <th className="px-4 py-2.5 text-left">OC Group</th>
                    <th className="px-4 py-2.5 text-left">Property</th>
                    <th className="px-4 py-2.5 text-left">Manager</th>
                    <th className="px-3 py-2.5 text-center w-32">Score</th>
                    <th className="px-3 py-2.5 text-center">Category</th>
                    <th className="px-3 py-2.5 text-center">Plans</th>
                    <th className="px-3 py-2.5 text-right">Group Revenue</th>
                    <th className="px-3 py-2.5 text-right">Gross Margin</th>
                    <th className="px-2 py-2.5 w-8"></th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-2.5 text-left">Plan</th>
                    <th className="px-4 py-2.5 text-left">Property</th>
                    <th className="px-4 py-2.5 text-left">Manager</th>
                    <th className="px-3 py-2.5 text-center w-32">Score</th>
                    <th className="px-3 py-2.5 text-center">Category</th>
                    <th className="px-3 py-2.5 text-right">Group Revenue</th>
                    <th className="px-3 py-2.5 text-right">Gross Margin</th>
                    <th className="px-2 py-2.5 w-8"></th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {filteredItems.map((item) => {
                const key = item.planNumber || item.basePlan || item.manager;
                const isExpanded = expandedItem === key;
                const hasComments = (item.comments || []).length > 0;
                const category = item.category || (item.avgScore >= 9 ? 'Promoter' : item.avgScore >= 7 ? 'Passive' : 'Detractor');
                const score = item.score || item.avgScore;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`transition-colors cursor-pointer ${isExpanded ? 'bg-[#F8F9FA]' : 'hover:bg-[#F8F9FA]/50'}`}
                      onClick={() => setExpandedItem(isExpanded ? null : key)}
                    >
                      {activeTab === 'managers' ? (
                        <>
                          <td className="px-4 py-3 text-xs font-medium text-[#171717]">{item.manager}</td>
                          <td className="px-3 py-3 text-center text-xs text-[#525252]">{item.planCount}</td>
                          <td className="px-3 py-3"><ScoreBar score={item.avgScore} /></td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs font-bold ${item.enps >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {item.enps > 0 ? '+' : ''}{item.enps}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-emerald-600 font-medium">{item.promoters}</td>
                          <td className="px-3 py-3 text-center text-xs text-amber-600 font-medium">{item.passives}</td>
                          <td className="px-3 py-3 text-center text-xs text-red-600 font-medium">{item.detractors}</td>
                          <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(item.totalRevenue)}</td>
                          <td className="px-3 py-3"><MarginCell profit={item.profit} margin={item.margin} /></td>
                        </>
                      ) : activeTab === 'groups' ? (
                        <>
                          <td className="px-4 py-3 font-mono text-xs font-medium text-[#171717]">{item.basePlan}</td>
                          <td className="px-4 py-3 text-xs text-[#525252] truncate max-w-[140px]">{item.propertyName || '-'}</td>
                          <td className="px-4 py-3 text-xs text-[#525252]">{item.manager}</td>
                          <td className="px-3 py-3"><ScoreBar score={item.score} /></td>
                          <td className="px-3 py-3 text-center"><CategoryBadge category={item.category} /></td>
                          <td className="px-3 py-3 text-center text-xs text-[#525252]">{item.planCount}</td>
                          <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(item.groupRevenue)}</td>
                          <td className="px-3 py-3"><MarginCell profit={item.profit} margin={item.margin} /></td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 font-mono text-xs font-medium text-[#171717]">{item.planNumber}</td>
                          <td className="px-4 py-3 text-xs text-[#525252] truncate max-w-[140px]">{item.propertyName || '-'}</td>
                          <td className="px-4 py-3 text-xs text-[#525252]">{item.manager}</td>
                          <td className="px-3 py-3"><ScoreBar score={item.score} /></td>
                          <td className="px-3 py-3 text-center"><CategoryBadge category={item.category} /></td>
                          <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(item.groupRevenue)}</td>
                          <td className="px-3 py-3"><MarginCell profit={item.isChildPlan ? null : item.profit} margin={item.margin} /></td>
                        </>
                      )}
                      <td className="px-2 py-3">
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-[#A3A3A3]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#A3A3A3]" />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={activeTab === 'managers' ? 10 : activeTab === 'groups' ? 9 : 8} className="px-6 py-4 bg-[#FAFAFA] border-b border-[#E5E5E5]">
                          <div className="space-y-4">
                            {/* Feedback & Action */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-2">Assessment</p>
                                <div className={`p-3 rounded-[10px] border ${
                                  category === 'Detractor' ? 'bg-red-50 border-red-200' :
                                  category === 'Passive' ? 'bg-amber-50 border-amber-200' :
                                  'bg-emerald-50 border-emerald-200'
                                }`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <CategoryBadge category={category} score={score} />
                                    <span className="text-xs font-medium text-[#171717]">{category}</span>
                                  </div>
                                  <p className="text-xs text-[#525252] leading-relaxed">
                                    {category === 'Detractor' && 'This client is dissatisfied. Immediate outreach required to understand concerns and develop an improvement plan. Risk of non-renewal is high.'}
                                    {category === 'Passive' && 'This client is satisfied but not enthusiastic. A proactive service review and relationship-building effort could convert them to a promoter and secure long-term retention.'}
                                    {category === 'Promoter' && 'This client is highly satisfied and likely to recommend your services. Maintain current service levels and consider them for testimonials or referrals.'}
                                  </p>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-2">Recommended Action</p>
                                <div className="p-3 rounded-[10px] border border-[#E5E5E5] bg-white space-y-2">
                                  {category === 'Detractor' && (
                                    <>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Schedule a face-to-face meeting with the client within the next 7 days</p></div>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Document specific pain points and create an action plan</p></div>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Follow up within 2 weeks to confirm improvements</p></div>
                                    </>
                                  )}
                                  {category === 'Passive' && (
                                    <>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Conduct a service quality review with the client</p></div>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Identify one area to exceed expectations this quarter</p></div>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Check in monthly to build stronger relationship</p></div>
                                    </>
                                  )}
                                  {category === 'Promoter' && (
                                    <>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Thank the client and acknowledge their feedback</p></div>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Ask for a testimonial or referral where appropriate</p></div>
                                      <div className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" /><p className="text-xs text-[#525252]">Ensure early renewal engagement to lock in retention</p></div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Comments */}
                            {hasComments && (
                              <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-2 flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3" strokeWidth={1.5} /> Client Feedback
                                </p>
                                <div className="space-y-2">
                                  {item.comments.map((comment, i) => (
                                    <div key={`${item.basePlan || item.planNumber || item.manager}-c-${i}-${comment.slice(0, 24)}`} className="text-xs text-[#525252] italic bg-white border border-[#E5E5E5] rounded-lg px-4 py-2.5 leading-relaxed">
                                      "{comment}"
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {!hasComments && (
                              <p className="text-xs text-[#A3A3A3] italic">No verbatim comments available for this {activeTab === 'managers' ? 'manager' : 'plan'}.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA] rounded-b-[10px]">
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#A3A3A3]">
            {filteredItems.length} {activeTab === 'managers' ? 'managers' : activeTab === 'groups' ? 'groups' : 'plans'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NPSAnalysis;
